import { useCallback } from 'react';
import { useQueryClient } from 'react-query';

import isEmpty from 'lodash/isEmpty';

import {
  ChangeProductsFiltersFunc,
  FetchProductsFilterFields,
  FetchProductsSort,
  FetchProductsSortTypes,
  OnProductSimilarSearchAction
} from '../../../../../products/productsTypes';
import { LifestyleDetectionParams } from '../../../../../lifestyleDetections/lifestyleDetectionsTypes';

import { useCurrentUser } from '../../../../../../auth/hooks/useAuth';
import { useReactQueryState } from '../../../../../common/reactQuery/hooks/useReactQueryState';

import {
  INITIAL_PRODUCTS_SORT,
  ProductsPermissions
} from '../../../../../products/productsConstants';

import { ProductSearchesCache } from '../../../../../productSearches/ProductsSearchCache';
import { LifestyleDetectionCache } from '../../../../../lifestyleDetections/LifestyleDetectionCache';

interface ThreeDStockProductsSearchOptions {
  changeProductsFilters?: ChangeProductsFiltersFunc;
  sortProducts?: (nextSort: FetchProductsSort) => void;
  isLibrary?: boolean;
}

function useThreeDStockProductsSearch({
  changeProductsFilters,
  sortProducts,
  isLibrary
}: ThreeDStockProductsSearchOptions) {
  const currentUser = useCurrentUser();
  const queryClient = useQueryClient();

  const { clearValue } = useReactQueryState<LifestyleDetectionParams>(
    LifestyleDetectionCache.searchImageUrlCacheKey()
  );

  const handleSearch = useCallback<(value: string) => void>(
    (value) => {
      const searchWithTaskName =
        isLibrary &&
        currentUser.hasPermissions(
          ProductsPermissions.READ_PRODUCTS_SEARCH_WITH_TASK_NAME
        );

      const removeFilters = value
        ? []
        : [
            FetchProductsFilterFields.NAME_SKU_TAGS,
            FetchProductsFilterFields.WITH_TASK_NAME
          ];

      changeProductsFilters(
        searchWithTaskName
          ? { nameSkuTags: value, withTaskName: true }
          : { nameSkuTags: value },
        removeFilters
      );

      const sort = isEmpty(removeFilters)
        ? [FetchProductsSortTypes.SCORE_DESC]
        : INITIAL_PRODUCTS_SORT;

      sortProducts(sort);

      queryClient.invalidateQueries(ProductSearchesCache.indexCacheKey());
    },
    [changeProductsFilters, currentUser, isLibrary, queryClient, sortProducts]
  );

  const onSearchSimilarImage = useCallback<(imageUrl: string) => void>(
    (imageUrl) => {
      clearValue();
      changeProductsFilters(
        {
          similarImage: {
            imageUrl,
            projectId: null,
            taskId: null
          }
        },
        [
          FetchProductsFilterFields.SIMILAR_PRODUCT,
          FetchProductsFilterFields.PRODUCT_CATEGORY_ID,
          FetchProductsFilterFields.PRODUCT_PARENT_CATEGORY_IDS,
          FetchProductsFilterFields.SIMILAR_PRODUCTS_BY_CUSTOM_AREA,
          FetchProductsFilterFields.ID,
          FetchProductsFilterFields.SEARCH_BY_DETECTED_ID
        ]
      );
      sortProducts([FetchProductsSortTypes.SCORE_DESC]);
    },
    [changeProductsFilters, clearValue, sortProducts]
  );

  const onSearchSimilarProduct = useCallback<OnProductSimilarSearchAction>(
    (product) => {
      changeProductsFilters({
        similarProduct: {
          imageId: product.image?.id,
          productId: product.id
        }
      });
      sortProducts([FetchProductsSortTypes.SCORE_DESC]);
    },
    [changeProductsFilters, sortProducts]
  );

  const onClearSearchSimilarImage = useCallback<() => void>(() => {
    changeProductsFilters({}, [
      FetchProductsFilterFields.SIMILAR_IMAGE,
      FetchProductsFilterFields.SIMILAR_PRODUCT
    ]);
    sortProducts(INITIAL_PRODUCTS_SORT);
  }, [changeProductsFilters, sortProducts]);

  return {
    handleSearch,
    onSearchSimilarImage,
    onSearchSimilarProduct,
    onClearSearchSimilarImage
  };
}

export default useThreeDStockProductsSearch;
